import * as yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import { smsAlerts } from "./booking";
import { addressNull } from "../helpers/address";

export const loginFormDefaults = {
  email: "",
  password: "",
};

export const loginFormSchema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{6,}$/, "Must be 6 or more characters and contain at least 1 number and 1 letter."),
});

export const registerFormDefaults = (data) => ({
  firstName: data?.firstName || "",
  lastName: data?.lastName || "",
  email: data?.email || "",
  phone: data?.phone || "",
  password: "",
  confirmPassword: "",
  companyName: data?.companyName || "",
});

export const businessFieldsSchema = yup.object().shape({
  companyName: yup.string().required("Company name is required"),
});

export const registerFormSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().optional(),
  email: yup.string().email().required("Email is required"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("phone-validation", "Invalid format!", (value) => isValidPhoneNumber(value)),
  password: yup
    .string()
    .required("Password is required")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{6,}$/, "Must be 6 or more characters and contain at least 1 number and 1 letter."),
  confirmPassword: yup.string().oneOf([yup.ref("password"), null], "Passwords must match"),
});

export const businessInfoFormDefaults = {
  serviceAddress: "",
  needs: [],
  closedBy: "",
};

export const businessInfoFormSchema = yup.object().shape({
  serviceAddress: yup.string().required("Service Address is required"),
  closedBy: yup.string().optional(),
  // salesContactEmail: yup.string().email().optional(),
  needs: yup.array().min(1, "Needs are required"),
});

export const businessUserFormSchema = yup.object().shape({
  companyName: yup.string(),
  closedBy: yup.string().optional(),
});

export const businessUserFormDefaults = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  companyName: "",
  closedBy: "",
};

export const guestRegisterFormDefaults = (data) => ({
  companyName: data?.companyName || "",
  fullName: data?.fullName || "",
  email: data?.email || "",
  phone: data?.phone || "",
  address: data?.address || "",
  distanceRange: data?.distanceRange || "200",
  alerts: data?.alerts,
});

export const guestRegisterFormSchema = yup.object().shape({
  companyName: yup.string().required("Company Name is required"),
  fullName: yup.string().required("Full Name is required"),
  email: yup.string().email().required("Email is required"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("phone-validation", "Invalid format!", (value) => isValidPhoneNumber(value)),
  address: yup
    .object()
    .nullable()
    .shape({
      zipCode: yup.string().required("Cannot find zipcode"),
    })
    .required("Zip code is required"),
});

export const registerFormPasswordDefaults = (data) => ({
  password: "",
  confirmPassword: "",
});

export const registerFormPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{6,}$/, "Must be 6 or more characters and contain at least 1 number and 1 letter."),
  confirmPassword: yup.string().oneOf([yup.ref("password"), null], "Passwords must match"),
});

export const noValidationSchema = yup.object().shape({});

export const businessUserContactFormDefaults = (data) => ({
  companyName: data?.companyName || "",
  fullName: data?.fullName || "",
  email: data?.email || "",
  phone: data?.phone || data?.phoneNumber || "",
  isAccountingDetailsSame: data?.isAccountingDetailsSame || false,
  accountingEmail: data?.accountingEmail || "",
  accountingPhone: data?.accountingPhone || "",
});

export const businessUserContactFormSchema = yup.object().shape({
  companyName: yup.string().required("Company Name is required"),
  fullName: yup.string().required("Full Name is required"),
  email: yup.string().email().required("Email is required"),
  phone: yup
    .string()
    .required("Phone Number is required")
    .test("phone-validation", "Invalid format!", (value) => isValidPhoneNumber(value)),

  isAccountingDetailsSame: yup.boolean(),
  accountingEmail: yup.string().when("isAccountingDetailsSame", {
    is: true,
    then: yup.string(),
    otherwise: yup.string().email().required("Email is required"),
  }),
  accountingPhone: yup.string().when("isAccountingDetailsSame", {
    is: true,
    then: yup.string(),
    otherwise: yup
      .string()
      .required("Phone Number is required")
      .test("phone-validation", "Invalid format!", (value) => isValidPhoneNumber(value)),
  }),
});

export const businessUserAdministrativeFormDefaults = (data) => ({
  fein: data?.F_EIN || "",
  epaDetails: data?.epaDetails?.length
    ? data.epaDetails
    : data?.EPA_ID
    ? [{ epaId: data.EPA_ID, address: addressNull }]
    : [{ epaId: "", address: addressNull }],
  w9Forms: data?.w9Forms || [],
  epaCertifications: data?.epaCertifications || [],
  additionalPermits: data?.additionalPermits || [],
});

export const businessUserAdministrativeFormSchema = yup.object().shape({
  fein: yup
    .string()
    .required("FEIN is required")
    .matches(/^[0-9]{2}-[0-9]{7}$/, "Invalid FEIN format. It should be XX-XXXXXXX."),
  epaDetails: yup.array().of(
    yup.object().shape({
      epaId: yup.string().optional(),
      address: yup
        .object()
        .nullable(true)
        .optional()
        .when("epaId", {
          is: (epaId) => !!epaId,
          then: yup.object().required("Address is required if EPA ID is provided"),
        }),
    })
  ),
  w9Forms: yup.array().of(yup.string().required("Item is required")).min(1, "W9 form is required").required("W9 form is required"),
  epaCertifications: yup.array(),
  additionalPermits: yup.array(),
});
export const businessUserTaskPreferencesFormDefaults = (data) => ({
  taskAvailable: data?.taskAvailable || smsAlerts.defaults.taskAvailable,
  notifications: data?.notifications || smsAlerts.defaults.businessAlerts,
  isNationwide: data?.isNationwide || false,
  location: data?.location || smsAlerts.defaults.location,
  address: data?.address || {},
  priceRange: data?.priceRange || smsAlerts.defaults.businessPriceRange,
});

export const businessUserTaskPreferencesFormSchema = yup.object().shape({
  taskAvailable: yup
    .array()
    .of(yup.string().required("Item is required"))
    .min(1, "At least one task option is required")
    .required("At least one task option is required"),
  notifications: yup
    .array()
    .of(yup.object().required("Item is required"))
    .min(1, "At least one notification option is required")
    .required("At least one notification option is required"),
  isNationwide: yup.boolean(),
  location: yup.string(),
  address: yup.mixed().when(["isNationwide", "location"], {
    is: (isNationwide, location) => !isNationwide && location === "address",
    then: yup.object().shape({ isNationwide: yup.boolean().required("Please specify your coverage area to continue") }),

    otherwise: yup.object().notRequired(),
  }),
  priceRange: yup
    .array()
    .of(yup.string().required("Item is required"))
    .min(1, "At least one price range option is required")
    .required("At least one price range option is required"),
});
